import React, { useState, useEffect } from 'react';
import { 
    BringzzText,
    BringzzIcon 
} from '@bringzz/components';
import { useOrder } from '../../context/OrderContext';
import DefaultProductImage from 'assets/DefaultProductImage.png';

const ProductScanDisplay = ({ 
    title = "Scanned Products",
    currentProductIndex,
    setCurrentProductIndex
}) => {
    const { orderDetails, prescriptionInfo } = useOrder();
    const [localProductIndex] = useState(0);
    
    // Use passed index if provided, otherwise use local state
    const activeIndex = currentProductIndex !== undefined ? currentProductIndex : localProductIndex;
    
    // Get products from orderDetails context
    const products = orderDetails?.products || [];
    const currentProduct = products[activeIndex] || {};
    
    const formatBarcode = (barcode = '1234567890123') => {
        // Format barcode for display (e.g., add spaces for readability)
        const code = String(barcode);
        return code.match(/.{1,4}/g)?.join(' ') || code;
    };
    
    const generateBarcodeLines = (barcode = '1234567890123') => {
        // Generate visual barcode representation
        const code = String(barcode);
        return code.split('').map((digit, index) => {
            const height = parseInt(digit) % 2 === 0 ? 'h-8' : 'h-10';
            const width = parseInt(digit) % 3 === 0 ? 'w-1' : 'w-0.5';
            return (
                <div 
                    key={index} 
                    className={`bg-black ${height} ${width} mx-0.5`}
                />
            );
        });
    };

    // Show message if no products available
    if (!products || products.length === 0) {
        return (
            <div className="flex flex-col h-full bg-white">
                <div className="p-4 border-b">
                    <BringzzText tag="h2" className="font-semibold text-lg">
                        {title}
                    </BringzzText>
                </div>
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center">
                        <BringzzIcon 
                            icon="IconBox"
                            folder="LineIcons"
                            size="48"
                            className="text-gray-400 mb-4"
                        />
                        <BringzzText className="text-gray-500">
                            No products available to display
                        </BringzzText>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                    <BringzzText tag="h2" className="font-semibold text-lg">
                        {title}
                    </BringzzText>
                    <BringzzText className="text-sm text-gray-500">
                        {activeIndex + 1} / {products.length}
                    </BringzzText>
                </div>
            </div>
            
            {/* Product Image */}
            <div className="flex-1 overflow-auto">
                <div className="p-6 flex flex-col items-center">
                    <div className="w-48 h-48 mb-4 rounded-lg overflow-hidden border border-gray-200">
                        <img 
                            src={currentProduct.productImage || currentProduct.photo || DefaultProductImage}
                            alt={currentProduct.name || 'Product'}
                            className="w-full h-full object-contain p-2"
                            onError={(e) => {
                                e.target.src = DefaultProductImage;
                            }}
                        />
                    </div>
                    
                    {/* Product Details */}
                    <div className="w-full max-w-md space-y-3">
                        <div className="text-center">
                            <BringzzText tag="h3" className="font-semibold text-lg">
                                {currentProduct.name || 'Product Name'}
                            </BringzzText>
                            <BringzzText className="text-gray-500 text-sm">
                                {currentProduct.manufacturer || ''}
                            </BringzzText>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 py-3 border-t border-b">
                            <div>
                                <BringzzText className="text-xs text-gray-500">
                                    Package
                                </BringzzText>
                                <BringzzText className="font-medium">
                                    {currentProduct.packaging || '---'} | {currentProduct.packageQuantity || '---'}
                                </BringzzText>
                            </div>
                            <div className="text-right">
                                <BringzzText className="text-xs text-gray-500">
                                    Price
                                </BringzzText>
                                <BringzzText className="font-semibold text-lg">
                                    € {currentProduct.price ? parseFloat(currentProduct.price).toFixed(2) : '0.00'}
                                </BringzzText>
                            </div>
                        </div>
                        
                        {currentProduct.description && (
                            <div className="py-2">
                                <BringzzText className="text-sm text-gray-600">
                                    {currentProduct.description}
                                </BringzzText>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Barcode Display - Full width */}
                <div className="px-6 pb-6">
                    <BringzzText className="text-xs text-gray-500 text-center mb-2">
                        Barcode
                    </BringzzText>
                    <div className="bg-white p-4 border rounded-lg">
                        <div className="flex justify-center items-end mb-2">
                            {generateBarcodeLines(currentProduct.barcodeProduct || '4150083785626')}
                        </div>
                        <BringzzText className="text-center text-xs font-mono">
                            {formatBarcode(currentProduct.barcodeProduct || '4150083785626')}
                        </BringzzText>
                    </div>
                </div>
                
                <div className="px-6">
                    <div className="w-full max-w-md mx-auto space-y-3">
                        
                        {/* Quantity Display */}
                        <div className="flex justify-between items-center py-2">
                            <BringzzText className="text-sm text-gray-600">
                                Quantity
                            </BringzzText>
                            <div className="flex items-center space-x-2">
                                <BringzzIcon 
                                    icon="IconBox"
                                    folder="LineIcons"
                                    size="16"
                                />
                                <BringzzText className="font-medium">
                                    {currentProduct.quantity || 1}
                                </BringzzText>
                            </div>
                        </div>

                        {/* Show prescription info if available */}
                        {prescriptionInfo && (
                            <div className="py-2 border-t">
                                <BringzzText className="text-xs text-gray-500 mb-1">
                                    Prescription Information
                                </BringzzText>
                                <div className="space-y-1">
                                    {prescriptionInfo.rezId && (
                                        <div className="flex justify-between">
                                            <BringzzText className="text-xs text-gray-600">Rez ID:</BringzzText>
                                            <BringzzText className="text-xs">{prescriptionInfo.rezId}</BringzzText>
                                        </div>
                                    )}
                                    {prescriptionInfo.eMedId && (
                                        <div className="flex justify-between">
                                            <BringzzText className="text-xs text-gray-600">E-Med ID:</BringzzText>
                                            <BringzzText className="text-xs">{prescriptionInfo.eMedId}</BringzzText>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductScanDisplay;